import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
  CheckIcon, 
  ArrowPathIcon, 
  DocumentTextIcon,
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';

export interface CaseUpdateStep {
  id: string;
  name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'error';
  description?: string;
}

interface CaseUpdateProgressBarProps {
  steps: CaseUpdateStep[];
  currentStep?: string;
  error?: string;
}

const CaseUpdateProgressBar: React.FC<CaseUpdateProgressBarProps> = ({ 
  steps, 
  currentStep, 
  error 
}) => {
  const { t } = useTranslation();

  const getStepIcon = (step: CaseUpdateStep) => {
    switch (step.status) {
      case 'completed':
        return <CheckIcon className="h-5 w-5 text-green-600" />;
      case 'in_progress':
        return <ArrowPathIcon className="h-5 w-5 text-blue-600 animate-spin" />;
      case 'error':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />;
      default:
        return <div className="h-5 w-5 rounded-full border-2 border-gray-300" />;
    }
  };

  const getStepColor = (step: CaseUpdateStep) => {
    switch (step.status) {
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'in_progress':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'error':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          {t('lawsuit.caseUpdateProgress', 'Case Update Progress')}
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          {t('lawsuit.caseUpdateProgressDescription', 'We are processing your case update and strengthening your case.')}
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <div className="flex-shrink-0">
              {getStepIcon(step)}
            </div>
            <div className={`ml-4 flex-1 p-3 rounded-lg border ${getStepColor(step)}`}>
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium">
                  {step.name}
                </h4>
                {step.status === 'in_progress' && (
                  <span className="text-xs font-medium">
                    {t('common.processing', 'Processing...')}
                  </span>
                )}
                {step.status === 'completed' && (
                  <span className="text-xs font-medium">
                    {t('common.completed', 'Completed')}
                  </span>
                )}
                {step.status === 'error' && (
                  <span className="text-xs font-medium">
                    {t('common.error', 'Error')}
                  </span>
                )}
              </div>
              {step.description && (
                <p className="text-xs mt-1 opacity-75">
                  {step.description}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            {t('lawsuit.progress', 'Progress')}: {steps.filter(s => s.status === 'completed').length} / {steps.length}
          </span>
          <span>
            {Math.round((steps.filter(s => s.status === 'completed').length / steps.length) * 100)}%
          </span>
        </div>
        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{
              width: `${(steps.filter(s => s.status === 'completed').length / steps.length) * 100}%`
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default CaseUpdateProgressBar;